// NewConversationMenu — start a direct message or (for admins/HR) an org space.
// Direct + Team pick a person from the directory; Department picks a department;
// Company creates the all-hands space directly. The backend enforces the
// admin/HR/manager rules regardless; we hide the space options for non-admins.
import React, { useEffect, useMemo, useState } from 'react';
import { getDepartments } from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { conversationsApi } from '../api/conversationsApi';
import type { Directory } from '../hooks/useDirectory';

const SPACE_ROLES = ['tenant_owner', 'owner', 'admin', 'tenant_admin', 'company_admin', 'hr_manager'];
function canManageSpaces(roles?: string[]): boolean {
  return !!roles?.some((r) => SPACE_ROLES.includes(r.toLowerCase()));
}

interface Dept {
  id: string;
  name: string;
}

type Step = 'menu' | 'direct' | 'circle' | 'team' | 'dept';

// Build a Circle: a name + a hand-picked set of people from any team(s).
const CircleComposer: React.FC<{
  directory: Directory;
  selfId?: string;
  busy: boolean;
  onCreate: (title: string, memberIds: string[]) => void;
}> = ({ directory, selfId, busy, onCreate }) => {
  const [title, setTitle] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Record<string, string>>({}); // userId -> name
  const results = useMemo(
    () => directory.search(q).filter((p) => p.userId !== selfId).slice(0, 50),
    [directory, q, selfId],
  );
  const toggle = (id: string, name: string) =>
    setSelected((s) => {
      const n = { ...s };
      if (n[id]) delete n[id];
      else n[id] = name;
      return n;
    });
  const ids = Object.keys(selected);
  return (
    <div>
      {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Circle name (optional)…"
        className="mb-1 w-full rounded-lg border border-border px-2 py-1.5 text-sm text-charcoal placeholder:text-muted-foreground focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-200"
      />
      {ids.length > 0 && (
        <div className="mb-1 flex flex-wrap gap-1">
          {ids.map((id) => (
            <span key={id} className="flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700">
              {selected[id]}
              <button type="button" onClick={() => toggle(id, '')} aria-label="Remove">
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Add people…"
        className="mb-1 w-full rounded-lg border border-border px-2 py-1.5 text-sm text-charcoal placeholder:text-muted-foreground focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-200"
      />
      <ul className="max-h-44 overflow-y-auto">
        {results.length === 0 ? (
          <li className="px-1 py-2 text-sm text-muted-foreground">No matches.</li>
        ) : (
          results.map((p) => {
            const on = !!selected[p.userId];
            return (
              <li key={p.userId}>
                <button
                  type="button"
                  onClick={() => toggle(p.userId, p.name)}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-muted"
                >
                  <span className={`flex h-5 w-5 flex-none items-center justify-center rounded border text-[10px] ${on ? 'border-primary-500 bg-primary-500 text-white' : 'border-border text-transparent'}`}>
                    ✓
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-charcoal">{p.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{p.position || p.email || ''}</span>
                  </span>
                </button>
              </li>
            );
          })
        )}
      </ul>
      <button
        type="button"
        disabled={busy || ids.length === 0}
        onClick={() => onCreate(title.trim(), ids)}
        className="mt-1 w-full rounded-lg bg-primary-500 px-2 py-2 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-40"
      >
        Create circle{ids.length ? ` · ${ids.length}` : ''}
      </button>
    </div>
  );
};

interface Props {
  directory: Directory;
  selfId?: string;
  roles?: string[];
  onCreated: (conversationId: string) => void;
  onClose: () => void;
}

const PersonList: React.FC<{ directory: Directory; selfId?: string; busy: boolean; onPick: (id: string) => void }> = ({
  directory,
  selfId,
  busy,
  onPick,
}) => {
  const [q, setQ] = useState('');
  const results = useMemo(
    () => directory.search(q).filter((p) => p.userId !== selfId).slice(0, 50),
    [directory, q, selfId],
  );
  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search people…"
        className="mb-1 w-full rounded-lg border border-border px-2 py-1.5 text-sm text-charcoal placeholder:text-muted-foreground focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-200"
      />
      <ul className="max-h-56 overflow-y-auto">
        {results.length === 0 ? (
          <li className="px-1 py-2 text-sm text-muted-foreground">No matches.</li>
        ) : (
          results.map((p) => (
            <li key={p.userId}>
              <button
                type="button"
                disabled={busy}
                onClick={() => onPick(p.userId)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-muted disabled:opacity-50"
              >
                <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
                  {p.name.slice(0, 2).toUpperCase()}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm text-charcoal">{p.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{p.position || p.email || ''}</span>
                </span>
              </button>
            </li>
          ))
        )}
      </ul>
    </>
  );
};

export const NewConversationMenu: React.FC<Props> = ({ directory, selfId, roles, onCreated, onClose }) => {
  const [step, setStep] = useState<Step>('menu');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [depts, setDepts] = useState<Dept[] | null>(null);
  const admin = canManageSpaces(roles);

  useEffect(() => {
    if (step === 'dept' && depts === null) {
      getDepartments()
        .then((r) => setDepts(((r.items as Dept[]) ?? []).map((d) => ({ id: d.id, name: d.name }))))
        .catch(() => setDepts([]));
    }
  }, [step, depts]);

  const run = async (fn: () => Promise<{ id: string }>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const conv = await fn();
      onCreated(conv.id);
    } catch (e) {
      // Say what the SERVER said. This catch used to discard the error
      // entirely and assert "you may not have permission", so an entitlement
      // that was switched off, a validation failure and a 500 were all
      // reported to the user as a permissions problem — a guess the UI is in
      // no position to make, and one that sends people to the wrong fix.
      // conversation-service already answers with a usable reason (e.g.
      // "conversations is not enabled for this workspace").
      setError(getApiErrorMessage(e, 'Could not create the conversation — please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="absolute inset-x-2 top-full z-20 mt-1 rounded-xl border border-border bg-card p-2 shadow-lg">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {step === 'menu'
            ? 'New'
            : step === 'direct'
              ? 'Direct message'
              : step === 'circle'
                ? 'New circle'
                : step === 'team'
                  ? 'Team space (manager)'
                  : 'Department space'}
        </span>
        <button type="button" onClick={step === 'menu' ? onClose : () => setStep('menu')} className="px-1 text-sm text-muted-foreground hover:text-muted-foreground">
          {step === 'menu' ? '✕' : '←'}
        </button>
      </div>

      {error && <p className="mb-1 rounded bg-red-50 px-2 py-1 text-xs text-red-600">{error}</p>}

      {step === 'menu' && (
        <div className="flex flex-col gap-1">
          <button type="button" onClick={() => setStep('direct')} className="rounded-lg px-2 py-2 text-left text-sm text-charcoal hover:bg-muted/50">
            💬 Direct message
          </button>
          <button type="button" onClick={() => setStep('circle')} className="rounded-lg px-2 py-2 text-left text-sm text-charcoal hover:bg-muted/50">
            ⭕ New circle
          </button>
          {admin && (
            <>
              <button type="button" onClick={() => setStep('dept')} className="rounded-lg px-2 py-2 text-left text-sm text-charcoal hover:bg-muted/50">
                🏢 Department space
              </button>
              <button type="button" onClick={() => setStep('team')} className="rounded-lg px-2 py-2 text-left text-sm text-charcoal hover:bg-muted/50">
                👥 Team space
              </button>
              <button type="button" disabled={busy} onClick={() => run(() => conversationsApi.createSpace('COMPANY', { title: 'All-hands' }))} className="rounded-lg px-2 py-2 text-left text-sm text-charcoal hover:bg-muted disabled:opacity-50">
                📣 Company (all-hands)
              </button>
            </>
          )}
        </div>
      )}

      {step === 'direct' && (
        <PersonList directory={directory} selfId={selfId} busy={busy} onPick={(id) => run(() => conversationsApi.createDirect(id))} />
      )}
      {step === 'circle' && (
        <CircleComposer
          directory={directory}
          selfId={selfId}
          busy={busy}
          onCreate={(title, memberIds) => run(() => conversationsApi.createGroup(title, memberIds))}
        />
      )}
      {step === 'team' && (
        <PersonList directory={directory} selfId={selfId} busy={busy} onPick={(id) => run(() => conversationsApi.createSpace('TEAM', { contextId: id }))} />
      )}
      {step === 'dept' && (
        <ul className="max-h-56 overflow-y-auto">
          {depts === null ? (
            <li className="px-1 py-2 text-sm text-muted-foreground">Loading departments…</li>
          ) : depts.length === 0 ? (
            <li className="px-1 py-2 text-sm text-muted-foreground">No departments.</li>
          ) : (
            depts.map((d) => (
              <li key={d.id}>
                <button type="button" disabled={busy} onClick={() => run(() => conversationsApi.createSpace('DEPARTMENT', { contextId: d.id, title: d.name }))} className="w-full rounded-lg px-2 py-1.5 text-left text-sm text-charcoal hover:bg-muted disabled:opacity-50">
                  {d.name}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};
